import { z } from 'zod';

import type {
  Account,
  Breed,
  Donor,
  Sire,
  Site,
  Yard,
} from '@/rtk-query.gql';

type GqlRef<T> = Pick<T, Extract<keyof T, 'id' | 'name' | 'code'>>;

const isRef = (val: unknown) =>
  !!val && typeof val === 'object' && 'id' in val && !!val.id;

const optionalRef = <T>() =>
  z.custom<GqlRef<T>>((val) => !val || isRef(val)).nullish();

export const nonTogEmbryoFormSchema = z
  .object({
    donor: z.custom<GqlRef<Donor>>(
      isRef,
      'onFarmImplant.nonTog.validation.donor',
    ),
    sire: z.custom<GqlRef<Sire>>(isRef, 'onFarmImplant.nonTog.validation.sire'),
    freezeOwner: z.custom<GqlRef<Account>>(
      isRef,
      'onFarmImplant.nonTog.validation.owner',
    ),
    breed: optionalRef<Breed>(),
    method: z.number().int().nullish(),
    recoveryFirmCode: z.string().nullish(),
    recoveryFirmName: z.string().nullish(),
    freshFrozen: z
      .number({ message: 'onFarmImplant.nonTog.validation.freshFrozen' })
      .int(),
    sex: z.number().int().nullish(),
    implantDate: z.date({
      message: 'onFarmImplant.nonTog.validation.implantDate',
    }),
    transferQuantity: z.number().int(),
    implantTierOf: z.number().int().nullish(),
    loadDonorPartners: z.number().int().nullish(),
    transferSite: z.custom<GqlRef<Site>>(
      isRef,
      'onFarmImplant.nonTog.validation.transferSite',
    ),
    transferClinicRanchHaulIn: z
      .number({ message: 'onFarmImplant.nonTog.validation.clinicRanchHaulIn' })
      .int(),
    transferTechName: z.string().nullish(),
    implantType: z.coerce.number().nullish(),
    recipientOwner: optionalRef<Account>(),
    deyard: optionalRef<Yard>(),
    transferOwner: optionalRef<Account>(),
  })
  .refine((data) => data.transferQuantity > 0, {
    message: 'onFarmImplant.nonTog.validation.count',
    path: ['transferQuantity'],
  });

export type NonTogEmbryoFormValues = z.infer<typeof nonTogEmbryoFormSchema>;

export const NON_TOG_GENERAL_FIELDS = [
  'donor',
  'sire',
  'freezeOwner',
  'freshFrozen',
] as const satisfies readonly (keyof NonTogEmbryoFormValues)[];
